import { createDebugLogger } from "@utils/debugLog";

const log = createDebugLogger("embed:bridge");

/**
 * Every message on the bridge carries this, so unrelated postMessage traffic on
 * the page — and there is plenty of it inside a model-driven app — is ignored
 * cheaply before anything else is inspected.
 */
export const EMBED_MESSAGE_CHANNEL = "onetasklist-embed";

/** What the host page can tell the app about where it is running. */
export interface EmbedHostContext {
  /** The Dataverse organisation's client URL, e.g. https://contoso.crm11.dynamics.com. */
  orgUrl: string;
  /** The signed-in Dataverse user. Identifies the host session, not the OneTaskList one. */
  userId: string;
  userName: string;
  /** The host's current appearance, so the embed can match it. */
  theme: "light" | "dark";
}

export type EmbedHostMessage =
  | { channel: typeof EMBED_MESSAGE_CHANNEL; type: "context"; payload: EmbedHostContext }
  | { channel: typeof EMBED_MESSAGE_CHANNEL; type: "theme"; payload: { theme: "light" | "dark" } };

export type EmbedAppMessage =
  | { channel: typeof EMBED_MESSAGE_CHANNEL; type: "ready" }
  | { channel: typeof EMBED_MESSAGE_CHANNEL; type: "resize"; payload: { height: number } };

/**
 * Host suffixes every Dataverse organisation lives under: the commercial cloud,
 * US Government (GCC High and DoD), and China.
 *
 * This is defence in depth rather than the primary control — the browser
 * already refuses to frame the app at all unless the parent matches
 * frame-ancestors, which the server sets from the same list. Keep in step with
 * DefaultEmbedFrameAncestors in
 * src/OneTaskList.Region.Web/Middleware/SecurityHeadersMiddleware.cs.
 */
const DATAVERSE_HOST_SUFFIXES = [
  ".dynamics.com",
  ".microsoftdynamics.us",
  ".appsplatform.us",
  ".dynamics.cn",
] as const;

/** True when `origin` is an https Dataverse organisation allowed to drive the bridge. */
export const isAllowedEmbedOrigin = (origin: string): boolean => {
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }

  if (url.protocol !== "https:") {
    return false;
  }

  // endsWith on a dot-prefixed suffix, so "contoso-dynamics.com" does not pass
  // as a subdomain of dynamics.com.
  const host = url.hostname.toLowerCase();
  return DATAVERSE_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));
};

const isThemeName = (value: unknown): value is "light" | "dark" =>
  value === "light" || value === "dark";

const isHostContext = (value: unknown): value is EmbedHostContext => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate["orgUrl"] === "string" &&
    typeof candidate["userId"] === "string" &&
    typeof candidate["userName"] === "string" &&
    isThemeName(candidate["theme"])
  );
};

const parseHostMessage = (data: unknown): EmbedHostMessage | null => {
  if (typeof data !== "object" || data === null) {
    return null;
  }

  const candidate = data as Record<string, unknown>;
  if (candidate["channel"] !== EMBED_MESSAGE_CHANNEL) {
    return null;
  }

  if (candidate["type"] === "context" && isHostContext(candidate["payload"])) {
    return { channel: EMBED_MESSAGE_CHANNEL, type: "context", payload: candidate["payload"] };
  }

  if (candidate["type"] === "theme") {
    const payload = candidate["payload"];
    if (
      typeof payload === "object" &&
      payload !== null &&
      isThemeName((payload as Record<string, unknown>)["theme"])
    ) {
      return {
        channel: EMBED_MESSAGE_CHANNEL,
        type: "theme",
        payload: { theme: (payload as Record<string, unknown>)["theme"] as "light" | "dark" },
      };
    }
  }

  return null;
};

export interface EmbedBridgeHandlers {
  onContext?: (context: EmbedHostContext) => void;
  onTheme?: (theme: "light" | "dark") => void;
}

export interface EmbedBridge {
  /** Asks the host to resize the frame. No-op until the host has been heard from. */
  requestResize: (height: number) => void;
  /** Removes the listener. */
  stop: () => void;
}

/**
 * Listens for the host and answers it.
 *
 * The host speaks first and keeps retrying until it gets "ready" back, which is
 * how the two sides meet without either guessing the other's origin or timing:
 * the app learns the host's origin from the first valid message and replies to
 * exactly that origin, never to "*".
 */
export const startEmbedBridge = (handlers: EmbedBridgeHandlers = {}): EmbedBridge => {
  let hostOrigin: string | null = null;

  const post = (message: EmbedAppMessage): void => {
    if (hostOrigin === null) {
      log("post: no host has been heard from yet, dropping", { type: message.type });
      return;
    }

    window.parent.postMessage(message, hostOrigin);
  };

  const onMessage = (event: MessageEvent): void => {
    if (event.source !== window.parent || !isAllowedEmbedOrigin(event.origin)) {
      return;
    }

    const message = parseHostMessage(event.data);
    if (message === null) {
      return;
    }

    if (hostOrigin === null) {
      hostOrigin = event.origin;
      log("bridge established", { hostOrigin });
    }

    if (message.type === "context") {
      handlers.onContext?.(message.payload);
      handlers.onTheme?.(message.payload.theme);
      // Acknowledges the handshake; the host stops retrying once it lands.
      post({ channel: EMBED_MESSAGE_CHANNEL, type: "ready" });
      return;
    }

    handlers.onTheme?.(message.payload.theme);
  };

  window.addEventListener("message", onMessage);
  log("listening for the host");

  return {
    requestResize: (height: number) =>
      post({ channel: EMBED_MESSAGE_CHANNEL, type: "resize", payload: { height } }),
    stop: () => window.removeEventListener("message", onMessage),
  };
};
